/**
 * O painel do dia (3.9; reformulado na 11.4, decisão 18) — a tela Hoje.
 *
 * **Três blocos**: os 4 KPIs (Tarefas vencidas, Para hoje, No vermelho, Sem próximo passo), a
 * fila do dia (as minhas Tarefas com prazo até hoje, concluídas em dois cliques) e "Negócios que
 * pedem ação" — os Negócios em andamento no vermelho da régua, sem próxima tarefa ou com tarefa
 * vencida, cada linha com o **motivo por extenso**. As três faixas de temperatura saíram.
 *
 * O recorte de visibilidade é do serviço (RLS): para escopo `todas` os KPIs de Negócio e a lista
 * são do time; a fila é sempre pessoal. O valor da SDR não vem na resposta (FR-62) — a tela
 * escreve a frase do recorte no lugar, e não um zero.
 *
 * Só o **nome** do Negócio é link, e ele abre o modal do Negócio sobre Hoje (`LinkDoNegocio`).
 * Os nomes das Empresas saem de um lote só (AD-25) para as duas listas.
 */

import { Link } from '@tanstack/react-router'
import { type MouseEvent, useMemo, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useNomesDeEmpresas, useNomesDePessoas } from '@/modulos/cadastro/api'
import { dias } from '@/modulos/negocios/ReguaDeDias'
import {
  type Funil,
  type MotivoDaAcao,
  type NegocioNoQuadro,
  type Regua,
  type SugestaoDeProximoPasso,
  type Tarefa,
  useItensDePrevisao,
  usePainelDeHoje,
} from '@/modulos/negocios/api'
import {
  MarcaDePrevisaoNoCard,
  type PrevisaoNoCard,
  previsaoNoCard,
} from '@/modulos/negocios/MarcaDePrevisao'
import {
  CONTATO_A_CAMINHO,
  EMPRESA_A_DEFINIR,
  FILA_VAZIA,
  LEGENDA_DA_FILA,
  MOEDA,
  NUMERO,
  PRAZO_VENCIDO,
  SEM_ORCAMENTO,
  TITULO_DA_FILA,
  corteDaFila,
  prazoNaLista,
  sinalDaCor,
} from '@/modulos/negocios/apresentacao'
import { LinkDoNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { useEhCelular } from '@/shell/Shell'
import { ConclusaoDeTarefa } from './ConclusaoDeTarefa'
import { NovaTarefaDaSugestao } from './NovaTarefaDaSugestao'
import { type OrigemDoTituloDaTarefa, tituloDaTarefa } from './regras-de-tarefa'
import './painel-de-hoje.css'
import './tarefas.css'

const ID_DOS_INDICADORES = 'painel-de-hoje-indicadores'
const ID_DA_FILA = 'painel-de-hoje-fila'
const ID_DOS_QUE_PEDEM_ACAO = 'painel-de-hoje-pedem-acao'

const NOME_DOS_INDICADORES = 'Números do dia'
/*
 * O título da fila vem de `apresentacao.ts` desde a 4.6: a tela de Tarefas escreve o mesmo
 * vocabulário sobre o mesmo dado, e duas cópias divergiriam no primeiro ajuste.
 */
const NOME_DA_FILA = TITULO_DA_FILA

/** O título do cartão da lista (EXPERIENCE.md, 11.4) — as faixas de temperatura saíram. */
const NOME_DOS_QUE_PEDEM_ACAO = 'Negócios que pedem ação'

/**
 * O nome acessível da lista, **diferente** do título do cartão de propósito.
 *
 * A lista mora dentro da região que o cartão já nomeia, e repetir o nome faria o leitor de tela
 * anunciar o mesmo rótulo duas vezes, aninhado. É a mesma decisão que `QuadroDoFunil` tomou para a
 * lista única do celular. "mais parados" é o que a ordenação do serviço de fato produz.
 */
const NOME_DA_LISTA = 'Negócios que pedem ação, do mais parado para o menos parado'

/** O que uma célula mostra quando o valor não existe ou não pôde ser resolvido. */
const VAZIO = '—'

const CARREGANDO = 'Carregando o painel do dia…'
const TENTAR_DE_NOVO = 'Tentar de novo'

/**
 * O que a fila escreve enquanto a leitura não chegou — ou quando ela foi recusada.
 *
 * Ela é **muda** de propósito: os cartões saem da **mesma** consulta, e anunciá-la duas vezes
 * faria o leitor de tela ouvir a recusa em duplicata, com duas saídas para o mesmo botão. A região
 * viva e a saída ficam num lugar só — o cartão dos Negócios que pedem ação —, e aqui fica a frase
 * que explica por que a fila está vazia.
 *
 * E ela é distinta de `FILA_VAZIA`, que é a **boa notícia** ("nada vencido e nada para hoje"):
 * escrever aquela aqui afirmaria que o dia está limpo justamente quando a tela não conseguiu
 * olhar para o dia.
 */
const FILA_SEM_LEITURA = 'A fila aparece quando a leitura do dia chegar.'

/**
 * O estado vazio da carteira — e ele é digno, e não um "nenhum resultado".
 *
 * Quem abre o CRM e não tem Negócio nenhum precisa saber que a tela está certa e onde se cadastra
 * o primeiro. Uma lista vazia sem frase pareceria defeito justamente para quem ainda não conhece
 * o produto.
 */
/**
 * O vazio da lista dos Negócios que pedem ação — frase **própria**, de Negócio: a de tarefas
 * ("Nada vencido nem para hoje.") é a da fila, e dita aqui falaria de tarefa num cartão de Negócio.
 */
export const SEM_NEGOCIOS_QUE_PEDEM_ACAO = 'Nenhum negócio pede ação agora.'

/** O rodapé que declara o corte da lista (11.4): os N mostrados, de quantos pedem ação. */
export function corteDosQuePedemAcao(mostrados: number, total: number): string {
  return `Os ${NUMERO.format(mostrados)} mais parados, de ${NUMERO.format(total)}`
}

/**
 * A recusa do catálogo de Funis.
 *
 * Ela **não** derruba a tela: sem o catálogo, o painel continua contando e listando — o que se
 * perde é o nome da Etapa de cada linha e a navegação para o quadro. Por isso ela é um aviso ao
 * lado, e não o corpo inteiro, ao contrário do quadro do Funil (lá o catálogo é pré-condição:
 * sem Funil escolhido a leitura nem sai).
 */
const SEM_CATALOGO =
  'Não foi possível carregar os funis e as etapas. As linhas abaixo saem sem a etapa e sem o link ' +
  'para o quadro.'

/**
 * O aviso de que os nomes das Empresas não vieram — e que o código na linha **não** quer dizer
 * "esta ficha não é sua".
 *
 * Ele existe porque a degradação silenciosa é indistinguível do recorte de visibilidade: uma linha com
 * o código é legítima e rara (FR-30); uma lista inteira delas, por causa de um `GET /empresas`
 * recusado, é defeito.
 */
const NOMES_NAO_VIERAM =
  'Não foi possível carregar os nomes das empresas; as linhas mostram o código do negócio ou o ' +
  'título da tarefa.'

/** O que o leitor de tela ouve enquanto o nome da Empresa ainda está no ar. */
const NOME_A_CAMINHO = ' (carregando o nome da empresa)'

/**
 * A nota do KPI "Tarefas vencidas" — o que o número conta, dito em três palavras.
 *
 * Ela deixou de prometer o Épico 4 na 4.6: o número existe. O que ela diz agora é o **recorte**,
 * que é a única coisa que o número sozinho não conta — são as Tarefas de quem está olhando, e não
 * as do time, mesmo para a gestão. Sem isso, um Gestor leria "2" e procuraria as duas na carteira
 * inteira.
 */
const NOTA_DAS_VENCIDAS = 'na fila do dia, primeiro'

/** A nota do KPI "Para hoje", pelo mesmo motivo e com o mesmo recorte. */
const NOTA_DE_HOJE = 'na fila do dia'

/**
 * A nota do KPI "Sem próximo passo", e ela explica **o que** o número conta.
 *
 * O texto do protótipo é literal (`:1085`, "nenhuma tarefa agendada"), e desde a 4.6 ele é
 * verdade: o serviço conta os Negócios em andamento que nenhuma Tarefa **pendente** aponta. Até
 * aqui o KPI contava **todo** Negócio em andamento, com uma nota que dizia "até o Épico 4 dar
 * tarefa a eles" — e isso passaria a ser falso na mesma tela em que a fila mostra tarefas de
 * verdade.
 *
 * **Ele conta Negócios, e não Tarefas**, e por isso não tem o recorte por Responsável dos dois
 * KPIs acima: o escopo dele é o que a policy entrega, o mesmo das três faixas.
 *
 * **E a nota diz isso**, em vez de deixar o "suas" dos dois vizinhos contaminar a leitura. Sem o
 * "que você enxerga", um Gestor leria três números lado a lado e suporia o mesmo recorte nos três
 * — dois deles só dele, e o terceiro do time inteiro. É a mesma correção que `ESCOPO_DAS_TAREFAS`
 * faz ao lado das abas da tela de Tarefas: onde o escopo muda, a tela o escreve.
 */
const NOTA_SEM_PROXIMO_PASSO = 'negócios em andamento sem tarefa'

/**
 * A nota do cartão da lista: **de onde os dias saem**, dita no tempo verbal certo.
 *
 * `ultimo_contato_em` tem escritor único (`registrar_contato`), e os dois gatilhos que o chamam
 * são concluir uma Tarefa e receber `mensagem_whatsapp_enviada` (Épico 8). **O primeiro existe
 * desde a 4.4**, e é por isso que a nota voltou ao presente na 4.6: concluir uma Tarefa presa a um
 * Negócio zera a contagem dele, na mesma transação, e a fila logo acima é o lugar de onde isso é
 * feito. Entre a 3.9 e a 4.3 a frase do protótipo descrevia um mecanismo que instalação nenhuma
 * conseguia disparar, e a nota dizia isso; agora ela descreve o que a tela faz.
 *
 * O WhatsApp fica de fora do texto porque continua não existindo: a nota diz o gatilho que há.
 * Quando não houve contato nenhum, a contagem corre desde a abertura do Negócio
 * (`COALESCE(ultimo_contato_em, data_de_inicio)`).
 */
const NOTA_DOS_QUE_PEDEM_ACAO = 'no vermelho, sem próxima tarefa ou com tarefa vencida'
/** O link do rodapé da fila (protótipo v2): a fila é o recorte de hoje; a tela de Tarefas é tudo. */
export const VER_TODAS = 'ver todas'
/** A frase da régua no rodapé dos que pedem ação (protótipo v2), com os cortes da régua vigente. */
export function fraseDaRegua(regua: Regua): string {
  return (
    `Verde até ${dias(regua.dias_para_amarelo)} · amarelo até ` +
    `${String(regua.dias_para_vermelho)} · vermelho acima. ` +
    'Concluir tarefa ou mandar WhatsApp zera a contagem.'
  )
}

/**
 * O motivo de cada linha, por extenso (11.4) — texto, e não só cor. `Record<MotivoDaAcao, …>` é o
 * guarda de exaustividade: um motivo novo no serviço reprova o `tsc` aqui.
 */
export const ROTULOS_DOS_MOTIVOS: Record<MotivoDaAcao, string> = {
  no_vermelho: 'No vermelho',
  sem_proxima_tarefa: 'Sem próxima tarefa',
  tarefa_vencida: 'Tarefa vencida',
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * Por que o título da linha não é a razão social — as **quatro** causas, separadas.
 *
 * É o mesmo tipo de `QuadroDoFunil`, e ele é repetido aqui em vez de importado de propósito: as
 * duas telas descrevem o mesmo fenômeno, mas um `export` do quadro faria o painel depender do
 * componente vizinho para existir. A separação que importa — `carregando` ≠ `falhou` ≠ "não é sua"
 * — vem de `NomesDeEmpresas`, que é a fonte comum das duas.
 */
type OrigemDoTitulo = 'resolvido' | 'pendente' | 'indisponivel' | 'ausente'

/**
 * O que a tela desenha por linha da lista — o view-model, montado uma vez para as duas árvores.
 *
 * Ele existe para que computador e celular escrevam **o mesmo texto**: dois cálculos paralelos
 * seriam duas chances de o celular mostrar "sem orçamento" onde o computador mostra um número, e
 * a paridade da NFR-1 viraria coincidência.
 */
interface LinhaQuePedeAcao {
  id: string
  /** A razão social; o código do Negócio quando ela não veio (por qualquer das outras causas). */
  titulo: string
  /** Qual das quatro causas produziu o título acima. */
  origemDoTitulo: OrigemDoTitulo
  /** 13.20b2: o Negócio sem Empresa — o título é o contato, e a linha diz "Empresa a definir". */
  semEmpresa: boolean
  /** O nome da Etapa, ou vazio quando o catálogo em cache não a conhece. */
  etapa: string
  /**
   * O texto do dinheiro, ou `null` quando a chave não veio (recorte da SDR, FR-62): aí o valor
   * **não é desenhado** — nem traço, nem frase —, como no modal do Negócio (State Patterns).
   */
  valor: string | null
  dias: string
  /** A frase por extenso que acompanha o ponto colorido — a mesma informação, sem depender de cor. */
  sinal: string
  cor: NegocioNoQuadro['cor']
  motivos: MotivoDaAcao[]
  /** A Previsão de fechamento (11.8) — a mesma marca do card do Funil (Component Patterns). */
  previsao: PrevisaoNoCard
}

/** Um KPI do topo, já resolvido para texto. */
interface Indicador {
  chave: string
  rotulo: string
  valor: string
  nota: string
  /** O tom do número — o mesmo do protótipo: vermelho, âmbar ou a cor do corpo. */
  tom: 'vermelho' | 'amarelo' | 'neutro'
}

/**
 * O ponto colorido com o texto ao lado — o padrão de acessibilidade da régua, repetido aqui.
 *
 * O ponto é `aria-hidden` porque ele é **decorativo**: quem não distingue as cores continua lendo
 * a mesma informação por escrito, no elemento ao lado. Cor sozinha nunca é o único portador de
 * significado nesta tela.
 */
function PontoDaCor({ cor }: { cor: NegocioNoQuadro['cor'] }) {
  return <span className={`hoje__ponto hoje__ponto--${cor}`} aria-hidden="true" />
}

/** O título da linha, com o aviso de "ainda estou esperando o nome" quando for o caso. */
function TituloDaLinha({ linha, classe }: { linha: LinhaQuePedeAcao; classe: string }) {
  const pendente = linha.origemDoTitulo === 'pendente'
  return (
    <span className={classe} data-nome={linha.origemDoTitulo} aria-busy={pendente || undefined}>
      {linha.titulo}
      {pendente ? (
        <span className="hoje__so-para-leitor">
          {linha.semEmpresa ? CONTATO_A_CAMINHO : NOME_A_CAMINHO}
        </span>
      ) : null}
    </span>
  )
}

/**
 * Uma linha de "Negócios que pedem ação": só o **nome** é link (abre o modal sobre Hoje); ao lado,
 * a Etapa, o valor (ou o recorte da SDR), os motivos por extenso e os dias na cor da régua.
 */
function ItemQuePedeAcao({ linha, classe }: { linha: LinhaQuePedeAcao; classe: string }) {
  return (
    <li className={classe} data-cor={linha.cor}>
      <div className="hoje__linha">
        <PontoDaCor cor={linha.cor} />
        <span className="hoje__linha-corpo">
          <LinkDoNegocio
            negocioId={linha.id}
            className="hoje__linha-nome"
            aria-label={`Abrir ${linha.titulo}`}
          >
            <TituloDaLinha linha={linha} classe="hoje__linha-titulo" />
          </LinkDoNegocio>
          <span className="hoje__linha-meta">
            {linha.semEmpresa ? (
              <span className="hoje__linha-sem-empresa">{EMPRESA_A_DEFINIR}</span>
            ) : null}
            {/* A Etapa em branco é o caso do catálogo que não a conhece: a linha continua. */}
            {linha.etapa === '' ? null : <span className="hoje__linha-etapa">{linha.etapa}</span>}
            {linha.valor === null ? null : <span className="hoje__linha-valor">{linha.valor}</span>}
            {linha.motivos.map((motivo) => (
              <span className="hoje__linha-motivo" key={motivo} data-motivo={motivo}>
                {ROTULOS_DOS_MOTIVOS[motivo]}
              </span>
            ))}
            <MarcaDePrevisaoNoCard
              previsao={linha.previsao}
              classe="hoje__linha-previsao"
              classeIndisponivel="hoje__linha-previsao-indisponivel"
            />
          </span>
        </span>
        <span className="hoje__linha-dias numero" data-cor={linha.cor}>
          {linha.dias}
          <span className="hoje__so-para-leitor">{` sem contato · ${linha.sinal}`}</span>
        </span>
      </div>
    </li>
  )
}

/**
 * O título de uma linha da fila, com a causa que o produziu.
 *
 * As **cinco** causas são as de `OrigemDoTitulo` mais a `solta` — o vínculo que a Tarefa não tem
 * (FR-20), que é de outra natureza: não é ausência de nome, é ausência de Empresa. A `origem` viaja
 * junto do texto porque é ela que o CSS lê (`data-nome`) e é ela que decide o `aria-busy`.
 */
interface LinhaDaFila {
  titulo: string
  origemDoTitulo: OrigemDoTituloDaTarefa
}

/**
 * Uma linha da fila do dia — o chip do Tipo, a Empresa, o título, o prazo e "Concluir" (4.6).
 *
 * **Ela não é um `<Link>`**, ao contrário da linha dos que pedem ação, e a diferença é o que cada uma
 * responde. Aquela leva à ficha do Negócio, que é o destino óbvio de um card do quadro. Esta tem
 * uma **ação** — concluir —, e o gesto que ela oferece acontece aqui mesmo, num modal: transformar
 * a linha inteira em link poria a ação dentro de um destino, e um clique fora do botão levaria
 * embora quem só queria concluir. E há um segundo motivo, estrutural: a Tarefa pode ser **solta**
 * ou ligada só a uma Empresa (FR-20), e nesses dois casos não existe ficha para onde ir.
 *
 * **As classes são as dos que pedem ação**, e não um segundo conjunto: as duas listas moram na mesma
 * tela, uma ao lado da outra, e uma gramática visual própria para cada uma faria a tela parecer
 * dois produtos. O que ela empresta de `tarefas.css` é só o chip do Tipo, o prazo e o botão — os
 * três elementos que a tela de Tarefas também desenha, e que não podem divergir entre as duas.
 */
function ItemDaFila({
  tarefa,
  titulo,
  classe,
  aoConcluir,
}: {
  tarefa: Tarefa
  /** O título e a causa dele — a escada de cinco de `tituloDaTarefa`. */
  titulo: LinhaDaFila
  classe: string
  aoConcluir: (tarefa: Tarefa, evento: MouseEvent<HTMLButtonElement>) => void
}) {
  const pendente = titulo.origemDoTitulo === 'pendente'
  return (
    /* `data-vencida` no `<li>` pinta a borda esquerda do cartão do celular de vermelho, no lugar
       onde a linha dos que pedem ação põe a cor da régua. É decoração: o prazo ao lado já diz por
       escrito, e `PRAZO_VENCIDO` o diz a quem não enxerga cor. */
    <li className={`${classe} hoje__fila-item`} data-vencida={tarefa.vencida}>
      {/* O Tipo como chip com o nome. O protótipo v2 desenha a caixinha com o **ícone** do Tipo,
          mas a Tarefa da fila não carrega o ícone e a tela Hoje não pode pedir o catálogo de
          Tipos ao montar (DW-299, orçamento do painel — há teste que o prova): o ícone chega
          quando `tipo_icone` viajar na própria Tarefa. */}
      <span className="tela-tarefas__tipo hoje__fila-tipo">{tarefa.tipo_nome}</span>
      <span className="hoje__linha-corpo">
        {/* `data-nome` e `aria-busy` são os mesmos da linha dos que pedem ação: a causa do título viaja
            para o CSS e para o leitor de tela, e `NOME_A_CAMINHO` é o que diz "o nome está a
            caminho" a quem não vê a diferença de cor. */}
        {/* A Empresa é link quando a Tarefa tem Negócio (protótipo v2, `.link-nome`): abre o
            modal sobre Hoje, como na lista ao lado. Solta ou só de Empresa, fica texto. */}
        {tarefa.negocio_id === null ? (
          <span
            className="hoje__linha-titulo"
            data-nome={titulo.origemDoTitulo}
            aria-busy={pendente || undefined}
          >
            {titulo.titulo}
            {pendente ? <span className="hoje__so-para-leitor">{NOME_A_CAMINHO}</span> : null}
          </span>
        ) : (
          <LinkDoNegocio
            negocioId={tarefa.negocio_id}
            className="hoje__linha-nome"
            aria-label={`Abrir ${titulo.titulo}`}
          >
            <span
              className="hoje__linha-titulo"
              data-nome={titulo.origemDoTitulo}
              aria-busy={pendente || undefined}
            >
              {titulo.titulo}
              {pendente ? <span className="hoje__so-para-leitor">{NOME_A_CAMINHO}</span> : null}
            </span>
          </LinkDoNegocio>
        )}
        <span className="hoje__linha-meta">
          <span className="hoje__fila-titulo">{tarefa.titulo}</span>
        </span>
      </span>
      {/* `vencida` vem do serviço, e a tela só pinta: a fila e o KPI "Tarefas vencidas" saem do
          **mesmo** carimbo de "hoje", e por isso não têm como discordar sobre a mesma linha. E o
          texto ao lado do vermelho é o que leva a informação a quem não enxerga cor. */}
      <span className="tela-tarefas__prazo" data-vencida={tarefa.vencida}>
        {prazoNaLista(tarefa.prazo)}
        {tarefa.vencida ? <span className="hoje__so-para-leitor">{PRAZO_VENCIDO}</span> : null}
      </span>
      {/* O nome acessível carrega o **título**: numa fila de seis linhas, seis botões chamados
          "Concluir" são indistinguíveis para quem navega por leitor de tela. */}
      <button
        type="button"
        className="tela-tarefas__acao"
        aria-label={`Concluir: ${tarefa.titulo}`}
        onClick={(evento) => {
          aoConcluir(tarefa, evento)
        }}
      >
        Concluir
      </button>
    </li>
  )
}

export interface PropsDoPainelDeHoje {
  /** Os Funis do catálogo, na ordem do serviço. Vazio enquanto `GET /funis` não respondeu. */
  funis: readonly Funil[]
  /**
   * A régua vigente, quando ela já chegou.
   *
   * Ela é **prop**, e não uma consulta daqui, porque a rota já a lê para a tela inteira — e é a
   * mesma chave de cache que o cartão de Administração usa. `undefined` é "ainda não sei", e a
   * legenda e a nota do KPI ficam em silêncio: um `?? 0` escreveria "acima de 0 dias sem contato",
   * que descreve uma régua que instalação nenhuma tem.
   */
  regua: Regua | undefined
  /**
   * A recusa de `GET /funis`, quando houve uma.
   *
   * Ela **não** derruba o painel — ao contrário do quadro, onde o catálogo é pré-condição. Aqui
   * ela custa o **nome da Etapa**, e mais nada: desde a 3.10 a linha navega para a ficha, que não
   * precisa do catálogo para abrir. O resto da tela continua verdadeiro.
   */
  erroDoCatalogo?: unknown
  /** Relê o catálogo. É a saída que a recusa de `GET /funis` precisa oferecer. */
  aoRecarregarOCatalogo?: () => void
}

export function PainelDeHoje({
  funis,
  regua,
  erroDoCatalogo,
  aoRecarregarOCatalogo,
}: PropsDoPainelDeHoje) {
  const ehCelular = useEhCelular()
  const consulta = usePainelDeHoje()

  const painel = consulta.data
  const itens = useMemo(() => painel?.pedem_acao ?? [], [painel])
  const fila = useMemo(() => painel?.fila ?? [], [painel])

  /*
   * Um pedido em lote para a tela inteira, e não um por linha (AD-25) — e **um** lote para as
   * **duas** listas, e não um por cartão: a Empresa que aparece na fila é quase sempre a mesma que
   * aparece na lista dos que pedem ação (é a carteira de quem está olhando), e dois lotes pediriam os mesmos ids
   * duas vezes. `useNomesDeEmpresas` já ordena e desduplica.
   *
   * A Tarefa **solta** não entra: ela não tem Empresa a resolver, e um `null` no lote viraria um
   * `?ids=` malformado.
   */
  const idsDeEmpresas = useMemo(
    () => [
      ...new Set([
        //: 13.20b1: o Negócio sem Empresa (`null`) fica fora do lote de nomes.
        ...itens.flatMap((item) => (item.empresa_id === null ? [] : [item.empresa_id])),
        ...fila
          .map((tarefa) => tarefa.empresa_do_vinculo_id)
          .filter((id): id is string => id !== null),
      ]),
    ],
    [itens, fila],
  )
  const empresas = useNomesDeEmpresas(idsDeEmpresas)
  /* 13.20b2: o contato do Negócio sem Empresa, num lote só do `cadastro` (AD-25). */
  const idsDeContatos = useMemo(
    () => [
      ...new Set(
        itens.flatMap((item) =>
          item.empresa_id === null && item.pessoa_id !== null ? [item.pessoa_id] : [],
        ),
      ),
    ],
    [itens],
  )
  const contatos = useNomesDePessoas(idsDeContatos)

  /* A Previsão (11.8): o card de "pedem ação" é o card do Funil, e a marca é a mesma. O catálogo só
     é pedido quando algum item tem previsão marcada. */
  const idsDePrevisao = useMemo(
    () => [
      ...new Set(
        itens.flatMap((item) =>
          item.previsao_de_fechamento_id == null ? [] : [item.previsao_de_fechamento_id],
        ),
      ),
    ],
    [itens],
  )
  const previsoes = useItensDePrevisao(idsDePrevisao, { habilitada: idsDePrevisao.length > 0 })

  /*
   * O título da linha da fila — a escada de cinco causas, que mora em `regras-de-tarefa.ts` porque
   * a tela de Tarefas escreve a mesma. Ela é a dos que pedem ação ao lado com um degrau a mais na
   * frente: a Tarefa **solta** (FR-20) não tem Empresa nenhuma, e é o único caso em que
   * `TAREFA_SEM_EMPRESA` é verdade.
   */
  const linhaDaFila = (tarefa: Tarefa): LinhaDaFila =>
    tituloDaTarefa(tarefa.empresa_do_vinculo_id, empresas)

  /*
   * O total de Tarefas com prazo **até hoje** — a soma dos dois KPIs, que contam tudo. É ele que o
   * rodapé compara com o tamanho da fila para dizer o corte: a fila é cortada no serviço
   * (`LIMITE_DA_FILA`) e as contagens não, e essa diferença é justamente o que a frase existe para
   * dizer. Derivá-lo dos KPIs, e não de uma constante copiada do serviço, é o mesmo desenho do
   * rodapé dos que pedem ação.
   */
  const ateHoje = (painel?.tarefas_vencidas ?? 0) + (painel?.tarefas_para_hoje ?? 0)

  /*
   * A Tarefa cujo modal de conclusão está aberto, e o gatilho a quem o foco volta — o mesmo
   * desenho do cartão da ficha e da tela de Tarefas.
   */
  const [concluindo, setConcluindo] = useState<Tarefa | null>(null)
  const gatilhoDaConclusao = useRef<HTMLElement | null>(null)

  /**
   * O **título do cartão da fila** — o alvo do foco quando o modal de próximo passo fecha.
   *
   * O gatilho da conclusão não serve aqui, e a razão é que ele deixou de existir: a conclusão deu
   * certo, a Tarefa deixou de ser pendente e a linha saiu da fila. Sem um alvo, o foco cairia no
   * `body` e quem navega por teclado teria de tabular a tela inteira para voltar onde estava.
   *
   * **O título é o alvo porque ele é o único elemento estável desta região**: ele não depende de
   * haver linha nenhuma (a fila pode ter esvaziado com a conclusão), ele nomeia o lugar de onde a
   * pessoa veio, e dali um `Tab` desce para a próxima linha — o caminho de quem quer cuidar da
   * seguinte. O `tabIndex={-1}` é o que o torna focável por código sem entrar na ordem de
   * tabulação, que é a diferença entre "levar o foco até aqui" e "pôr um `<h2>` no caminho de
   * todo mundo".
   *
   * **E o mesmo alvo serve às duas saídas do modal**, como a 4.5 já argumentou: recusar não pode
   * custar mais que aceitar, e dois destinos de foco fariam a tela tratar as duas respostas como
   * se uma delas fosse a errada.
   */
  const tituloDaFila = useRef<HTMLHeadingElement>(null)

  /** As duas saídas do modal de próximo passo devolvem o foco ao título da fila. */
  function focarOTituloDaFila() {
    tituloDaFila.current?.focus()
  }

  /**
   * A conclusão **deu certo** — e é isso que decide o destino do foco quando o modal fecha.
   *
   * Sem esta marca, os dois botões que concluem cairiam no ramo do gatilho, e "Concluir sem
   * resultado" (que não abre o modal de sugestão em seguida) deixaria o foco no `body`: a Tarefa
   * deixa de ser pendente, a linha sai da fila e o botão que abriu o modal deixa de existir. É a
   * mesma razão que o título da fila já tem para ser o alvo das duas saídas do modal de próximo
   * passo.
   */
  const concluiuDeVerdade = useRef(false)

  /*
   * A sugestão aceita, com o prazo que o modal calculou — ou `null`. É o que abre o `Dialogo` de
   * criação pré-preenchido. **O destino é o mesmo da tela de Tarefas**, e não o cartão da ficha:
   * aqui também não há formulário de criação na tela, e a Tarefa pode ser solta ou de Empresa.
   */
  const [sugestaoAceita, setSugestaoAceita] = useState<{
    tarefa: Tarefa
    sugestao: SugestaoDeProximoPasso
  } | null>(
    null,
  )

  /*
   * Abrir uma conclusão **fecha o diálogo de criação**, quando ele estiver aberto.
   *
   * Ele não deveria estar: o `Dialogo` cobre a tela. Mas ele não prende o foco (DW-180), e por
   * teclado dá para alcançar um "Concluir" de trás dele — e então a árvore teria **dois**
   * `role="dialog"` empilhados, um por cima do outro, com `Esc` fechando só o de cima. É a mesma
   * guarda que `ConclusaoDeTarefa` já faz por dentro, entre os dois modais dele; aqui ela vale
   * para o destino **injetado**, que é de fora.
   */
  function abrirAConclusao(tarefa: Tarefa, evento: MouseEvent<HTMLButtonElement>) {
    gatilhoDaConclusao.current = evento.currentTarget
    setSugestaoAceita(null)
    setConcluindo(tarefa)
  }

  function fecharAConclusao() {
    setConcluindo(null)
    const gatilho = gatilhoDaConclusao.current
    gatilhoDaConclusao.current = null
    /* Desistir (`Esc`, o clique no fundo, o "Cancelar") devolve o foco ao gatilho, que continua
       montado — ele vive na linha da fila, e não no diálogo. Concluir **não**: a linha sai da fila
       logo em seguida e aquele botão deixa de existir, e o foco cairia no `body`. */
    if (concluiuDeVerdade.current) {
      concluiuDeVerdade.current = false
      focarOTituloDaFila()
      return
    }
    gatilho?.focus()
  }

  /*
   * `etapa_id -> nome da Etapa`, montado **uma** vez do catálogo.
   *
   * O serviço não devolve o nome da Etapa no item (AD-25), e resolvê-lo por linha seria a ida à
   * rede por card que aquele AD existe para evitar.
   *
   * **Ele carregava também o `funil_id`, e deixou de carregar na 3.10.** Aquele campo era o
   * destino da navegação — a linha levava ao quadro do Funil da Etapa —, e com a ficha o destino
   * passou a ser `/negocios/{id}`, que não precisa do Funil para abrir. O que sobrou é o rótulo, e
   * a Etapa que o catálogo não conhece continua sem ele: a linha aparece com a Etapa em branco, e
   * navega igual.
   */
  const etapas = useMemo(() => {
    const mapa = new Map<string, string>()
    for (const funil of funis) {
      for (const etapa of funil.etapas) {
        mapa.set(etapa.id, etapa.nome)
      }
    }
    return mapa
  }, [funis])

  const linhas: LinhaQuePedeAcao[] = useMemo(
    () =>
      itens.map((item) => {
        const empresaId = item.empresa_id
        /* 13.20b2: sem Empresa, o nome é o do contato, pela mesma escada sobre o lote de Pessoas. */
        const nome =
          empresaId === null
            ? item.pessoa_id === null
              ? undefined
              : contatos.nomes.get(item.pessoa_id)
            : empresas.nomes.get(empresaId)
        const loteCarregando = empresaId === null ? contatos.carregando : empresas.carregando
        const loteFalhou = empresaId === null ? contatos.falhou : empresas.falhou
        /*
         * As causas, separadas. Só `ausente` — lote inteiro chegado e nome fora dele — é a
         * degradação documentada do FR-30. A ordem das guardas é a das certezas: `carregando`
         * antes de `falhou`, porque um lote ainda no ar pode trazer o nome.
         */
        const origemDoTitulo: OrigemDoTitulo =
          nome !== undefined
            ? 'resolvido'
            : loteCarregando
              ? 'pendente'
              : loteFalhou
                ? 'indisponivel'
                : 'ausente'
        /*
         * O recorte, medido no **corpo** e não no perfil: `valor` ausente é o FR-62 em ação
         * (`response_model_exclude_unset` no serviço), e `null` é "sem orçamento". Um perfil
         * ausente — sessão ainda no ar, um perfil novo que o SPA não conheça — não pode escrever
         * "sem orçamento" sobre um campo que o serviço recortou, e por isso a chave é quem manda.
         */
        const recortado = item.valor === undefined
        const daEtapa = etapas.get(item.etapa_id)
        return {
          id: item.id,
          /*
           * O nome que não veio vira o **código** do Negócio — o identificador que a operação já
           * usa na boca (AD-12). Sem `NUMERO`: `codigo` é identificador, e não quantidade.
           */
          titulo: nome ?? `Negócio ${String(item.codigo)}`,
          origemDoTitulo,
          semEmpresa: empresaId === null,
          etapa: daEtapa ?? '',
          valor: recortado
            ? null
            : item.valor === null
              ? SEM_ORCAMENTO
              : MOEDA.format(Number(item.valor)),
          dias: `${NUMERO.format(item.dias_sem_contato)}d`,
          sinal: sinalDaCor(item.cor),
          cor: item.cor,
          motivos: item.motivos,
          previsao: previsaoNoCard(previsoes, item.previsao_de_fechamento_id),
        }
      }),
    [
      itens,
      empresas.nomes,
      empresas.carregando,
      empresas.falhou,
      contatos.nomes,
      contatos.carregando,
      contatos.falhou,
      etapas,
      previsoes,
    ],
  )

  const noVermelho = painel?.no_vermelho ?? 0
  const totalQuePedeAcao = painel?.pedem_acao_total ?? 0

  const indicadores: Indicador[] = [
    {
      chave: 'tarefas-vencidas',
      rotulo: 'Tarefas vencidas',
      /*
       * O número vem do serviço desde a 4.6, e o `?? 0` só existe para o render em que o painel
       * ainda é `undefined` — ali `semLeitura` já troca o valor pelo traço logo abaixo, e é ela
       * quem decide, e não este operador. Um zero afirmado sem leitura seria a tela dizendo que
       * não há nada atrasado justamente quando ela não conseguiu olhar.
       */
      valor: NUMERO.format(painel?.tarefas_vencidas ?? 0),
      nota: NOTA_DAS_VENCIDAS,
      tom: 'vermelho',
    },
    {
      chave: 'para-hoje',
      rotulo: 'Para hoje',
      valor: NUMERO.format(painel?.tarefas_para_hoje ?? 0),
      nota: NOTA_DE_HOJE,
      tom: 'neutro',
    },
    {
      chave: 'no-vermelho',
      rotulo: 'No vermelho',
      valor: NUMERO.format(noVermelho),
      /*
       * Sem a régua não há número a afirmar, e a nota fica em silêncio em vez de mentir.
       *
       * O plural sai de `dias`, a mesma função que escreve o intervalo da faixa vermelha logo
       * abaixo: o corte é o **mesmo** número nas duas frases, e redigitar a regra aqui fazia a
       * tela discordar de si mesma numa régua de um dia ("acima de 1 dias" ao lado de "mais de
       * 1 dia"). `dias_para_vermelho` aceita `ge=0`, então uma régua assim é legítima.
       */
      nota: regua === undefined ? '' : `mais de ${dias(regua.dias_para_vermelho)} sem contato`,
      tom: 'vermelho',
    },
    {
      chave: 'sem-proximo-passo',
      rotulo: 'Sem próximo passo',
      /*
       * Desde a 4.6 ele conta os Negócios em andamento que **nenhuma Tarefa pendente** aponta, e
       * não mais `emAndamento` inteiro: o serviço faz a conta por `NOT EXISTS`, na mesma
       * transação. Contar todos aqui passaria a ser falso na mesma tela em que a fila abaixo
       * mostra tarefas de verdade.
       */
      valor: NUMERO.format(painel?.sem_proximo_passo ?? 0),
      nota: NOTA_SEM_PROXIMO_PASSO,
      tom: 'amarelo',
    },
  ]

  /*
   * O `!isError` na frente importa: numa recusa `consulta.data` também é `undefined`, e sem ele o
   * erro seria lido como "ainda carregando" — a tela sumiria e quem tomou um `500` ficaria sem
   * saída. É a mesma fórmula de `QuadroDoFunil` e de `ListaDeEmpresas`.
   *
   * O catálogo **não** entra nesta fórmula, e a diferença para o quadro é o desenho: lá, sem Funil
   * escolhido a consulta nem sai; aqui `GET /hoje` não tem parâmetro nenhum e responde
   * independentemente do catálogo.
   */
  const carregando = !consulta.isError && (consulta.isLoading || painel === undefined)
  /*
   * **Sem corpo lido não há número a afirmar** — e a recusa é tão "sem corpo" quanto a espera.
   *
   * `carregando` é falso numa recusa (o `!isError` na frente), e sem esta segunda condição
   * `faixas` cairia para `[]` e os KPIs escreveriam "No vermelho: 0" e "Sem próximo passo: 0" ao
   * lado do alerta de erro — a tela afirmando que a carteira está limpa justamente quando ela não
   * conseguiu olhar para a carteira. É a única confusão que este painel não pode cometer: ele
   * existe para denunciar abandono, e um zero inventado é a denúncia ao contrário.
   */
  const semLeitura = carregando || consulta.isError
  const vazio = !carregando && linhas.length === 0

  return (
    <div className="hoje">
      <section
        className="hoje__indicadores"
        aria-labelledby={ID_DOS_INDICADORES}
        aria-busy={carregando || undefined}
      >
        <h2 className="hoje__so-para-leitor" id={ID_DOS_INDICADORES}>
          {NOME_DOS_INDICADORES}
        </h2>
        <ul className="hoje__kpis">
          {indicadores.map((indicador) => (
            <li className="hoje__kpi" key={indicador.chave}>
              <span className="hoje__kpi-rotulo">{indicador.rotulo}</span>
              <span className="hoje__kpi-valor numero" data-tom={indicador.tom}>
                {/* Sem leitura, o número é o traço — na espera **e** na recusa. Um zero afirmaria
                    que não há Negócio no vermelho, que é justamente a coisa que a tela existe para
                    denunciar. */}
                {semLeitura ? VAZIO : indicador.valor}
              </span>
              {indicador.nota === '' ? null : (
                <span className="hoje__kpi-nota">{indicador.nota}</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* O aviso dos nomes vem **uma vez** para a tela inteira, e acima das duas colunas.
          Desde a 4.6 o **mesmo** lote alimenta as duas listas — a dos que pedem ação e a fila do dia —,
          e um aviso por cartão faria o leitor de tela ouvir a mesma recusa duas vezes. Ele também
          não pode morar dentro do cartão dos que pedem ação: quem não tem Negócio nenhum e tem Tarefa
          cairia no estado vazio daquele cartão, e a fila degradaria em silêncio ao lado.

          Ele vem **junto** com as listas, e não no lugar delas: o painel chegou, e o que falhou foi
          só o nome. */}
      {empresas.falhou ? (
        <p className="hoje__aviso hoje__aviso--recusa" role="alert">
          {NOMES_NAO_VIERAM}
        </p>
      ) : null}

      {erroDoCatalogo === undefined ? null : (
        <div className="hoje__recusa">
          <p className="hoje__erro" role="alert">
            {`${SEM_CATALOGO} ${mensagemDe(erroDoCatalogo)}`}
          </p>
          {aoRecarregarOCatalogo === undefined ? null : (
            <button type="button" className="hoje__botao" onClick={aoRecarregarOCatalogo}>
              {TENTAR_DE_NOVO}
            </button>
          )}
        </div>
      )}

      <div className="hoje__colunas">
        <section className="hoje__cartao" aria-labelledby={ID_DA_FILA}>
          <div className="hoje__cartao-cabecalho">
            <h2 className="hoje__cartao-titulo" id={ID_DA_FILA} ref={tituloDaFila} tabIndex={-1}>
              {NOME_DA_FILA}
            </h2>
            {/* A legenda diz as **duas** coisas que a fila promete: a ordem (o que venceu antes
                vem antes) e a forma do prazo (data pura, sem hora). */}
            <span className="hoje__cartao-nota">{LEGENDA_DA_FILA}</span>
          </div>

          {/* A ordem canônica, a mesma do cartão ao lado: erro → carregando → vazio → lista. O
              erro e o carregando são anunciados **uma vez só** na tela inteira, pelo cartão do
              cartão dos que pedem ação (é lá que mora a saída "Tentar de novo"): os dois cartões saem da **mesma**
              consulta, e dois `role="alert"` fariam o leitor de tela ouvir a recusa em duplicata.
              Aqui fica a frase muda que explica por que a fila está vazia. */}
          {semLeitura ? (
            <p className="hoje__aviso">{FILA_SEM_LEITURA}</p>
          ) : fila.length === 0 ? (
            /* O vazio da fila é a **boa notícia**: nada venceu e nada está marcado para hoje. */
            <p className="hoje__aviso">{FILA_VAZIA}</p>
          ) : (
            <>
              <ul
                className={ehCelular ? 'hoje__cartoes' : 'hoje__linhas'}
                aria-label={NOME_DA_FILA}
              >
                {fila.map((tarefa) => (
                  <ItemDaFila
                    key={tarefa.id}
                    tarefa={tarefa}
                    titulo={linhaDaFila(tarefa)}
                    classe={ehCelular ? 'hoje__item-empilhado' : 'hoje__item'}
                    aoConcluir={abrirAConclusao}
                  />
                ))}
              </ul>
              {/* O corte é **declarado**, no molde do rodapé dos que pedem ação: a fila é um recorte (as
                  suas com prazo até hoje), e não uma listagem. O total sai dos **dois KPIs**, que
                  contam tudo — e não de uma constante copiada do serviço. */}
              <p className="hoje__rodape">
                {ateHoje > fila.length ? `${corteDaFila(fila.length, ateHoje)} · ` : null}
                <Link className="hoje__ver-todas" to="/tarefas">
                  {VER_TODAS}
                </Link>
              </p>
            </>
          )}
        </section>

        <section className="hoje__cartao" aria-labelledby={ID_DOS_QUE_PEDEM_ACAO}>
          <div className="hoje__cartao-cabecalho">
            <h2 className="hoje__cartao-titulo" id={ID_DOS_QUE_PEDEM_ACAO}>
              {NOME_DOS_QUE_PEDEM_ACAO}
            </h2>
            <span className="hoje__cartao-nota">{NOTA_DOS_QUE_PEDEM_ACAO}</span>
          </div>

          {consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <div className="hoje__recusa">
              <p className="hoje__erro" role="alert">
                {mensagemDe(consulta.error)}
              </p>
              <button
                type="button"
                className="hoje__botao"
                onClick={() => {
                  void consulta.refetch()
                }}
              >
                {TENTAR_DE_NOVO}
              </button>
            </div>
          ) : carregando ? (
            <p className="hoje__aviso" role="status">
              {CARREGANDO}
            </p>
          ) : vazio ? (
            <p className="hoje__aviso">{SEM_NEGOCIOS_QUE_PEDEM_ACAO}</p>
          ) : (
            <>
              <ul
                className={ehCelular ? 'hoje__cartoes' : 'hoje__linhas'}
                aria-label={NOME_DA_LISTA}
              >
                {linhas.map((linha) => (
                  <ItemQuePedeAcao
                    key={linha.id}
                    linha={linha}
                    classe={ehCelular ? 'hoje__item-empilhado' : 'hoje__item'}
                  />
                ))}
              </ul>
              {/* O corte é **declarado** (11.4): só quando a lista não traz todos. */}
              {totalQuePedeAcao > linhas.length ? (
                <p className="hoje__rodape">
                  {corteDosQuePedemAcao(linhas.length, totalQuePedeAcao)}
                </p>
              ) : null}
              {regua === undefined ? null : (
                <p className="hoje__rodape hoje__rodape--regua">{fraseDaRegua(regua)}</p>
              )}
            </>
          )}
        </section>
      </div>

      {/* O fluxo de conclusão, o mesmo componente do cartão da ficha e da tela de Tarefas (4.6).

          **O destino do aceite é o desta tela**: um `Dialogo` de criação pré-preenchido, e não o
          formulário do cartão da ficha — aqui não há formulário na tela, e a Tarefa da fila pode
          ser solta ou ligada só a uma Empresa. */}
      <ConclusaoDeTarefa
        concluindo={concluindo}
        aoConcluir={() => {
          concluiuDeVerdade.current = true
        }}
        aoFechar={fecharAConclusao}
        aoAceitarASugestao={(tarefa, sugestao) => {
          setSugestaoAceita({ tarefa, sugestao })
          //: O foco **não** volta ao título aqui: o `Dialogo` de criação abre em seguida e leva o
          //: foco para dentro dele, que é onde a pessoa acabou de pedir para estar. Ele volta ao
          //: título quando aquele diálogo fechar.
        }}
        aoRecusarASugestao={focarOTituloDaFila}
      />

      {/* O destino injetado: "Nova tarefa" pré-preenchido, e **nenhum** `POST` até o submit
          (FR-22). Ele só existe como saída do aceite. */}
      {sugestaoAceita === null ? null : (
        <NovaTarefaDaSugestao
          origem={sugestaoAceita.tarefa}
          sugestao={sugestaoAceita.sugestao}
          aoFechar={() => {
            setSugestaoAceita(null)
            focarOTituloDaFila()
          }}
        />
      )}
    </div>
  )
}
